// src/components/DocSubjectSelect.js
import React, { useEffect, useMemo, useRef, useState } from "react";

const ALL_CATEGORIES = "__ALL__";
const OTHER_CATEGORY = "Other / Not Listed";

const DocSubjectSelect = ({ onSelect, onSelectMeta }) => {
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [loading, setLoading] = useState(true);
  const [taxonomy, setTaxonomy] = useState(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const customInputRef = useRef(null);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearch(search.trim().toLowerCase()),
      180
    );

    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let mounted = true;
    const cacheKey = "subjects.taxonomy.cache";

    const readCache = () => {
      try {
        return JSON.parse(localStorage.getItem(cacheKey) || "null");
      } catch {
        return null;
      }
    };

    const writeCache = (data) => {
      try {
        localStorage.setItem(cacheKey, JSON.stringify(data));
      } catch {
        // Taxonomy remains usable even when browser storage is unavailable.
      }
    };

    const cached = readCache();

    const load = async () => {
      try {
        const module = await import("../data/taxonomy/subjects.v1.json");
        const data = module.default || module;

        if (!cached || cached.version !== data.version) {
          writeCache(data);
        }

        if (mounted) setTaxonomy(data);
      } catch {
        if (mounted) {
          setTaxonomy({
            version: "fallback",
            order: ["Immigration", OTHER_CATEGORY],
            categories: {
              Immigration: {
                subjects: [
                  "Student Visa Application Letter",
                  "USCIS RFE Response",
                ],
              },
              [OTHER_CATEGORY]: {
                subjects: ["Other"],
              },
            },
          });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (cached) {
      setTaxonomy(cached);
      setLoading(false);
      load();
    } else {
      load();
    }

    return () => {
      mounted = false;
    };
  }, []);

  const categoryNames = useMemo(() => {
    if (!taxonomy) return [];

    const ordered = Array.isArray(taxonomy.order)
      ? taxonomy.order.filter(Boolean)
      : [];

    if (ordered.length) return ordered;

    return Object.keys(taxonomy.categories || {});
  }, [taxonomy]);

  const categoryOptions = useMemo(
    () => [
      { value: ALL_CATEGORIES, label: "All categories" },
      ...categoryNames.map((name) => ({
        value: name,
        label: name,
      })),
    ],
    [categoryNames]
  );

  const normalizedGroups = useMemo(() => {
    if (!taxonomy) return [];

    const categories = taxonomy.categories || {};
    const groups = [];

    for (const categoryName of categoryNames) {
      const node = categories[categoryName];
      if (!node) continue;

      if (Array.isArray(node.subjects)) {
        groups.push({
          category: categoryName,
          label: categoryName,
          subjects: node.subjects.filter(Boolean),
        });
        continue;
      }

      if (node.subgroups && typeof node.subgroups === "object") {
        for (const [subgroupName, subjects] of Object.entries(node.subgroups)) {
          if (!Array.isArray(subjects)) continue;

          groups.push({
            category: categoryName,
            label: `${categoryName} — ${subgroupName}`,
            subjects: subjects.filter(Boolean),
          });
        }
      }
    }

    return groups;
  }, [taxonomy, categoryNames]);

  const categoryFilteredGroups = useMemo(() => {
    if (selectedCategory === ALL_CATEGORIES) return normalizedGroups;

    return normalizedGroups.filter(
      (group) => group.category === selectedCategory
    );
  }, [normalizedGroups, selectedCategory]);

  const filteredGroups = useMemo(() => {
    if (!debouncedSearch) return categoryFilteredGroups;

    const query = debouncedSearch;
    const matches = [];

    for (const group of categoryFilteredGroups) {
      const groupMatches = group.label.toLowerCase().includes(query);
      const subjectMatches = group.subjects.filter((subject) =>
        String(subject).toLowerCase().includes(query)
      );

      if (groupMatches && subjectMatches.length === 0) {
        matches.push(group);
      } else if (subjectMatches.length > 0) {
        matches.push({
          ...group,
          subjects: subjectMatches,
        });
      }
    }

    return matches;
  }, [categoryFilteredGroups, debouncedSearch]);

  const totalOptions = useMemo(
    () =>
      filteredGroups.reduce(
        (total, group) => total + group.subjects.length,
        0
      ),
    [filteredGroups]
  );

  const customSubjectTrimmed = customSubject.trim();
  const customSubjectValid = customSubjectTrimmed.length >= 3;

  const emitSelection = ({
    subject,
    category,
    groupLabel = "",
    isOther = false,
    source,
  }) => {
    const cleanSubject = String(subject || "").trim();
    const cleanCategory =
      String(category || "").trim() ||
      (selectedCategory === ALL_CATEGORIES
        ? OTHER_CATEGORY
        : selectedCategory);

    onSelect?.(cleanSubject);
    onSelectMeta?.({
      subject: cleanSubject,
      category: cleanCategory,
      groupLabel,
      isOther,
      source,
    });
  };

  const handleCategoryChange = (event) => {
    const nextCategory = event.target.value;

    setSelectedCategory(nextCategory);
    setSelectedSubject("");
    setSearch("");

    emitSelection({
      subject: customSubjectTrimmed,
      category:
        nextCategory === ALL_CATEGORIES ? OTHER_CATEGORY : nextCategory,
      groupLabel: "",
      isOther: Boolean(customSubjectTrimmed),
      source: customSubjectTrimmed ? "custom" : "category_change",
    });
  };

  const handleSubjectChange = (event) => {
    const value = event.target.value;
    const selectedOption = event.target.selectedOptions?.[0];
    const category =
      selectedOption?.dataset?.category ||
      (selectedCategory === ALL_CATEGORIES
        ? OTHER_CATEGORY
        : selectedCategory);
    const groupLabel = selectedOption?.dataset?.groupLabel || "";

    setSelectedSubject(value);

    if (!value) {
      emitSelection({
        subject: customSubjectTrimmed,
        category:
          selectedCategory === ALL_CATEGORIES
            ? OTHER_CATEGORY
            : selectedCategory,
        groupLabel: "",
        isOther: Boolean(customSubjectTrimmed),
        source: customSubjectTrimmed ? "custom" : "cleared",
      });
      return;
    }

    if (value === "Other") {
      emitSelection({
        subject: customSubjectTrimmed,
        category,
        groupLabel: groupLabel || OTHER_CATEGORY,
        isOther: true,
        source: "custom",
      });

      window.requestAnimationFrame(() => {
        customInputRef.current?.focus();
      });
      return;
    }

    setCustomSubject("");

    emitSelection({
      subject: value,
      category,
      groupLabel,
      isOther: false,
      source: "taxonomy",
    });
  };

  const handleCustomSubjectChange = (event) => {
    const value = event.target.value;
    const cleanValue = value.trim();

    setCustomSubject(value);
    setSelectedSubject("");

    emitSelection({
      subject: cleanValue,
      category:
        selectedCategory === ALL_CATEGORIES
          ? OTHER_CATEGORY
          : selectedCategory,
      groupLabel: OTHER_CATEGORY,
      isOther: true,
      source: "custom",
    });
  };

  const clearSearch = () => {
    setSearch("");
  };

  const clearCustomSubject = () => {
    setCustomSubject("");

    emitSelection({
      subject: selectedSubject === "Other" ? "" : selectedSubject,
      category:
        selectedCategory === ALL_CATEGORIES
          ? OTHER_CATEGORY
          : selectedCategory,
      groupLabel: "",
      isOther: false,
      source: selectedSubject && selectedSubject !== "Other"
        ? "taxonomy"
        : "cleared",
    });
  };

  return (
    <section
      className="doc-subject-select"
      aria-label="Document subject selector"
      style={styles.section}
    >
      <style>{`
        .doc-subject-select * {
          box-sizing: border-box;
        }

        .doc-subject-select input,
        .doc-subject-select select,
        .doc-subject-select button {
          font: inherit;
        }

        .doc-subject-select input:focus-visible,
        .doc-subject-select select:focus-visible,
        .doc-subject-select button:focus-visible {
          outline: 3px solid rgba(30, 42, 120, 0.18);
          outline-offset: 2px;
        }

        @media (max-width: 640px) {
          .doc-subject-select {
            padding: 13px !important;
          }

          .doc-subject-inline-action {
            width: 100%;
          }
        }
      `}</style>


      <div style={styles.fieldStack}>
        <div>
          <label htmlFor="doc-subject-category" style={styles.fieldLabel}>
            Category
          </label>
          <select
            id="doc-subject-category"
            value={selectedCategory}
            onChange={handleCategoryChange}
            disabled={loading}
            aria-label="Filter subjects by category"
            style={{
              ...styles.field,
              ...(loading ? styles.disabledField : null),
            }}
          >
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div style={styles.fieldHelp}>
            Optional filter. You may leave this set to All categories.
          </div>
        </div>

        <div>
          <label htmlFor="doc-subject-search" style={styles.fieldLabel}>
            Search the subject library
          </label>

          <div style={styles.searchWrap}>
            <input
              id="doc-subject-search"
              type="search"
              inputMode="search"
              placeholder="Search subjects, such as CP2000, immigration, or lease"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              disabled={loading}
              aria-label="Search document subjects"
              style={{
                ...styles.field,
                paddingRight: search ? 86 : 12,
                ...(loading ? styles.disabledField : null),
              }}
            />

            {search && !loading ? (
              <button
                type="button"
                className="doc-subject-inline-action"
                onClick={clearSearch}
                style={styles.clearSearchButton}
                aria-label="Clear subject search"
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>

        <div>
          <label htmlFor="doc-subject-select" style={styles.fieldLabel}>
            Choose a matching subject
          </label>

          <select
            id="doc-subject-select"
            value={selectedSubject}
            onChange={handleSubjectChange}
            disabled={loading || totalOptions === 0}
            style={{
              ...styles.field,
              ...(loading || totalOptions === 0
                ? styles.disabledField
                : null),
            }}
          >
            <option value="">
              {loading
                ? "Loading subjects…"
                : totalOptions === 0
                ? "No matching library subjects"
                : `Select a subject (${totalOptions.toLocaleString()} result${
                    totalOptions === 1 ? "" : "s"
                  })`}
            </option>

            {filteredGroups.map((group) => (
              <optgroup
                key={`${group.category}:${group.label}`}
                label={group.label}
              >
                {group.subjects.map((subject) => (
                  <option
                    key={`${group.category}:${group.label}:${subject}`}
                    value={subject}
                    data-category={group.category}
                    data-group-label={group.label}
                  >
                    {subject}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          {totalOptions === 0 && !loading ? (
            <div style={styles.noMatches}>
              No library match was found. Enter your own subject below.
            </div>
          ) : null}
        </div>
      </div>

      <div style={styles.orDivider}>
        <span style={styles.orLine} />
        <span style={styles.orText}>or enter your own subject</span>
        <span style={styles.orLine} />
      </div>

      <div style={styles.customBox}>
        <div style={styles.customHeader}>
          <div>
            <label htmlFor="doc-custom-subject" style={styles.customLabel}>
              Type your own subject
            </label>
            <div style={styles.customHelp}>
              You do not need to select “Other” first.
            </div>
          </div>

          {customSubject ? (
            <button
              type="button"
              onClick={clearCustomSubject}
              style={styles.clearCustomButton}
            >
              Clear
            </button>
          ) : null}
        </div>

        <input
          ref={customInputRef}
          id="doc-custom-subject"
          type="text"
          value={customSubject}
          onChange={handleCustomSubjectChange}
          placeholder="Example: Letter requesting correction of an inaccurate collection account"
          aria-describedby="doc-custom-subject-status"
          style={{
            ...styles.customInput,
            ...(customSubject && !customSubjectValid
              ? styles.customInputPending
              : null),
            ...(customSubjectValid ? styles.customInputValid : null),
          }}
        />

        <div
          id="doc-custom-subject-status"
          aria-live="polite"
          style={{
            ...styles.customStatus,
            ...(customSubjectValid ? styles.customStatusValid : null),
          }}
        >
          {customSubject
            ? customSubjectValid
              ? "Your custom subject is ready."
              : "Enter at least 3 characters."
            : "Typing here will replace any library subject selection."}
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: {
    width: "100%",
    marginBottom: 20,
    border: "1px solid rgba(15, 23, 42, 0.11)",
    borderRadius: 15,
    background: "#FFFFFF",
    padding: 16,
    boxShadow: "0 7px 20px rgba(15, 23, 42, 0.045)",
  },


  fieldStack: {
    display: "grid",
    gap: 13,
  },

  fieldLabel: {
    display: "block",
    marginBottom: 6,
    color: "#0F172A",
    fontSize: 12.5,
    lineHeight: 1.35,
    fontWeight: 850,
  },

  field: {
    display: "block",
    width: "100%",
    minHeight: 43,
    border: "1px solid rgba(15, 23, 42, 0.16)",
    borderRadius: 10,
    background: "#FFFFFF",
    color: "#0F172A",
    padding: "10px 11px",
    fontSize: 13.5,
    lineHeight: 1.35,
    outline: "none",
    boxShadow: "none",
  },

  disabledField: {
    cursor: "not-allowed",
    opacity: 0.62,
    background: "#F8FAFC",
  },

  fieldHelp: {
    marginTop: 5,
    color: "#64748B",
    fontSize: 10.5,
    lineHeight: 1.4,
  },

  searchWrap: {
    position: "relative",
  },

  clearSearchButton: {
    position: "absolute",
    top: "50%",
    right: 6,
    transform: "translateY(-50%)",
    minHeight: 31,
    border: "1px solid rgba(15, 23, 42, 0.11)",
    borderRadius: 8,
    background: "#F8FAFC",
    color: "#475569",
    padding: "5px 9px",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 850,
  },

  noMatches: {
    marginTop: 6,
    borderRadius: 9,
    background: "#FFF7ED",
    color: "#9A3412",
    padding: "8px 9px",
    fontSize: 11,
    lineHeight: 1.45,
    fontWeight: 750,
  },

  orDivider: {
    margin: "17px 0 13px",
    display: "grid",
    gridTemplateColumns: "minmax(20px, 1fr) auto minmax(20px, 1fr)",
    alignItems: "center",
    gap: 9,
  },

  orLine: {
    height: 1,
    background: "rgba(15, 23, 42, 0.11)",
  },

  orText: {
    color: "#64748B",
    fontSize: 10.5,
    lineHeight: 1,
    fontWeight: 850,
    textTransform: "uppercase",
    letterSpacing: "0.045em",
  },

  customBox: {
    border: "1px solid rgba(30, 42, 120, 0.15)",
    borderRadius: 12,
    background: "#F8FAFF",
    padding: 12,
  },

  customHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 8,
  },

  customLabel: {
    display: "block",
    color: "#1E2A78",
    fontSize: 12.5,
    lineHeight: 1.35,
    fontWeight: 900,
  },

  customHelp: {
    marginTop: 3,
    color: "#64748B",
    fontSize: 10.5,
    lineHeight: 1.4,
    fontWeight: 700,
  },

  clearCustomButton: {
    flex: "0 0 auto",
    border: "1px solid rgba(30, 42, 120, 0.14)",
    borderRadius: 8,
    background: "#FFFFFF",
    color: "#1E2A78",
    padding: "5px 8px",
    cursor: "pointer",
    fontSize: 10.5,
    fontWeight: 850,
  },

  customInput: {
    display: "block",
    width: "100%",
    minHeight: 44,
    border: "1px solid rgba(30, 42, 120, 0.22)",
    borderRadius: 10,
    background: "#FFFFFF",
    color: "#0F172A",
    padding: "10px 11px",
    fontSize: 13.5,
    lineHeight: 1.35,
    outline: "none",
    boxShadow: "none",
  },

  customInputPending: {
    borderColor: "#F59E0B",
    boxShadow: "0 0 0 2px rgba(245, 158, 11, 0.08)",
  },

  customInputValid: {
    borderColor: "#16A34A",
    boxShadow: "0 0 0 2px rgba(22, 163, 74, 0.08)",
  },

  customStatus: {
    marginTop: 6,
    color: "#64748B",
    fontSize: 10.5,
    lineHeight: 1.4,
    fontWeight: 700,
  },

  customStatusValid: {
    color: "#15803D",
  },
};

export default DocSubjectSelect;
